/**
 * Feature API Tools
 *
 * Defines client-side agentic tools that wrap the CefSharp-bound FeatureApi.
 * Each tool maps to a method on the C# FeatureApi class exposed via CefSharp.
 *
 * CefSharp specifics:
 *   - Methods are camelCase on the JS side
 *   - All methods return Promise<string> (JSON-encoded)
 *   - Arguments must be passed positionally (not as an object)
 */

import { toToolDisplayName, type RuntimeToolDef } from "./runtimeTypes";
import { getAPI } from "../services/workspaceService";
import { invokeCefSharpMethod } from "../services/cefSharpBinding";

const TIMEOUT_MS = 30_000;

// ---------------------------------------------------------------------------
// Tool specifications
// ---------------------------------------------------------------------------

interface FeatureApiToolSpec {
  /** Method name on the JS-side featureApi object (camelCase). */
  method: string;
  /** Tool name exposed to the agent. */
  name: string;
  description: string;
  parameters: RuntimeToolDef["definition"]["parameters"];
  /** Ordered argument names matching the C# method signature. */
  argOrder: string[];
}

const TOOL_SPECS: FeatureApiToolSpec[] = [
  // ── Single feature ────────────────────────────────────────────────────
  {
    method: "getFeature",
    name: "WS-featureApi-getFeature",
    description:
      "Returns a JSON object with full detail for a single feature identified by its numeric ID. " +
      "Includes attributes, bounding box, phase, alternative, and version info.",
    parameters: {
      type: "object",
      properties: {
        featureId: {
          type: "number",
          description: "The numeric ID of the feature.",
        },
      },
      required: ["featureId"],
    },
    argOrder: ["featureId"],
  },
  {
    method: "getFeatureByGuid",
    name: "WS-featureApi-getFeatureByGuid",
    description:
      "Returns a JSON object with full detail for a single feature identified by its GUID. " +
      "Includes attributes, bounding box, phase, alternative, and version info.",
    parameters: {
      type: "object",
      properties: {
        guidString: {
          type: "string",
          description: "The GUID of the feature.",
        },
      },
      required: ["guidString"],
    },
    argOrder: ["guidString"],
  },

  // ── Feature attributes ────────────────────────────────────────────────
  {
    method: "getFeatureAttributes",
    name: "WS-featureApi-getFeatureAttributes",
    description:
      "Returns a JSON array of attributes for a single feature identified by its numeric ID. " +
      "Each attribute includes attributeTypeId, name, valueType, and value.",
    parameters: {
      type: "object",
      properties: {
        featureId: {
          type: "number",
          description: "The numeric ID of the feature.",
        },
      },
      required: ["featureId"],
    },
    argOrder: ["featureId"],
  },

  // ── Feature lists ─────────────────────────────────────────────────────
  {
    method: "getFeaturesByTask",
    name: "WS-featureApi-getFeaturesByTask",
    description:
      "Returns a paginated JSON list of features for a task identified by its GUID. " +
      "Response includes totalCount, offset, count, and a compact features array. " +
      "Start with a small limit (10–20) and page forward as needed.",
    parameters: {
      type: "object",
      properties: {
        taskGuid: {
          type: "string",
          description: "The GUID of the task definition.",
        },
        offset: {
          type: "number",
          description: "0-based start index for pagination.",
        },
        limit: {
          type: "number",
          description: "Maximum number of features to return.",
        },
      },
      required: ["taskGuid", "offset", "limit"],
    },
    argOrder: ["taskGuid", "offset", "limit"],
  },
  {
    method: "getFeaturesByType",
    name: "WS-featureApi-getFeaturesByType",
    description:
      "Returns a paginated JSON list of features filtered by feature type ID. " +
      "Response includes totalCount, offset, count, and a features array.",
    parameters: {
      type: "object",
      properties: {
        featureTypeId: {
          type: "number",
          description: "The numeric feature type ID to filter by.",
        },
        offset: {
          type: "number",
          description: "0-based start index for pagination.",
        },
        limit: {
          type: "number",
        },
      },
      required: ["featureTypeId", "offset", "limit"],
    },
    argOrder: ["featureTypeId", "offset", "limit"],
  },

  // ── Spatial search ────────────────────────────────────────────────────
  {
    method: "getFeaturesInBoundingBox",
    name: "WS-featureApi-getFeaturesInBoundingBox",
    description:
      "Returns a paginated JSON list of features within a 2D bounding box. " +
      "Coordinates are in model CRS. Response includes totalCount, offset, count, and features array. ",
    parameters: {
      type: "object",
      properties: {
        minX: {
          type: "number",
          description: "Minimum X coordinate of the bounding box.",
        },
        minY: {
          type: "number",
          description: "Minimum Y coordinate of the bounding box.",
        },
        maxX: {
          type: "number",
          description: "Maximum X coordinate of the bounding box.",
        },
        maxY: {
          type: "number",
          description: "Maximum Y coordinate of the bounding box.",
        },
        offset: {
          type: "number",
          description: "0-based start index for pagination.",
        },
        limit: {
          type: "number",
        },
      },
      required: ["minX", "minY", "maxX", "maxY", "offset", "limit"],
    },
    argOrder: ["minX", "minY", "maxX", "maxY", "offset", "limit"],
  },

  // ── AOI queries ───────────────────────────────────────────────────────
  {
    method: "getFeaturesOutsideAOI",
    name: "WS-featureApi-getFeaturesOutsideAOI",
    description:
      "Returns a JSON list of features that are geometrically outside the Area of Interest (AOI) polygon. " +
      "Response includes totalCount, offset, count, and a compact features array.",
    parameters: {
      type: "object",
      properties: {
        offset: {
          type: "number",
          description: "0-based start index (default 0).",
        },
      },
      required: ["offset"],
    },
    argOrder: ["offset"],
  },
  {
    method: "getFeaturesInsideAOI",
    name: "WS-featureApi-getFeaturesInsideAOI",
    description:
      "Returns a JSON list of features that are geometrically inside the Area of Interest (AOI) polygon. " +
      "Response includes totalCount, offset, count, and a compact features array.",
    parameters: {
      type: "object",
      properties: {
        offset: {
          type: "number",
          description: "0-based start index (default 0).",
        },
      },
      required: ["offset"],
    },
    argOrder: ["offset"],
  },
];

// ---------------------------------------------------------------------------
// CefSharp-aware callback factory
// ---------------------------------------------------------------------------

function createCefSharpCallback(
  method: string,
  argOrder: string[],
): (args: Record<string, unknown>) => Promise<string> {
  return async (args: Record<string, unknown>): Promise<string> => {
    try {
      const api = getAPI();
      const featureApi = api?.featureApi;
      if (!featureApi) {
        return JSON.stringify({
          success: false,
          error:
            "FeatureApi not bound — CefSharp.BindObjectAsync may not have completed.",
        });
      }

      const fn = featureApi[method];
      if (typeof fn !== "function") {
        return JSON.stringify({
          success: false,
          error: `Unknown method: featureApi.${method}`,
        });
      }

      const positionalArgs = argOrder.map((name) => args?.[name]);
      const raw = await invokeCefSharpMethod<string>(
        "featureApi",
        featureApi,
        method,
        positionalArgs,
      );

      let data: unknown;
      try {
        data = JSON.parse(raw);
      } catch {
        data = raw;
      }

      // Compact feature list responses to reduce token usage
      if (
        data &&
        typeof data === "object" &&
        Array.isArray((data as any).features)
      ) {
        const d = data as {
          totalCount: number;
          offset: number;
          count: number;
          features: any[];
        };
        d.features = d.features.map((f) => ({
          featureId: f.featureId,
          name: f.name,
          featureTypeName: f.featureTypeName,
        }));
      }

      return JSON.stringify({ success: true, data });
    } catch (error) {
      return JSON.stringify({
        success: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create RuntimeToolDef entries for every FeatureApi method.
 * Call this after `CefSharp.BindObjectAsync("featureApi")` has resolved.
 */
export function createFeatureApiTools(): Record<string, RuntimeToolDef> {
  const tools: Record<string, RuntimeToolDef> = {};

  for (const spec of TOOL_SPECS) {
    tools[spec.name] = {
      definition: {
        name: spec.name,
        description: spec.description,
        deferred: true,
        title: toToolDisplayName(spec.name),
        parameters: spec.parameters,
      },
      callback: createCefSharpCallback(spec.method, spec.argOrder),
      timeOutInMs: TIMEOUT_MS,
    };
  }

  return tools;
}
